import './ClientSelector.scss';
import cn from 'classnames';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { MdLanguage, MdOutlineAddPhotoAlternate } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFilteredList } from '@src/utils/search';
import { useAppSelector } from '../../../redux';
import { useNavBarState } from '../../../redux/navbar';
import * as UserRedux from '../../../redux/user';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { useVirtual } from '../../../utils/hooks/useVirtual';
import { Avatar } from '../../Avatar';
import { Button } from '../../Button';
import { Appearance, Background, Placement, Size } from '../../designConstants';
import { IconButton } from '../../IconButton';
import { SearchField } from '../../SearchField/SearchField';
import { EmptySearch } from '../EmptySearch/EmptySearch';
import { NAV_POPOVER_CONFIG, NavMenuKey } from '../navbar-constants';
import { useExperimentFlag } from '../../../utils/experiment-context';
import { useCloseNav, useNavHoverOpen, useNavTopMenu } from '../useNavBarPopover';
import { ClientLogo } from './ClientLogo';

const ITEM_HEIGHT = 52;
const OVERSCAN = 2;
const MAX_LIST_HEIGHT = ITEM_HEIGHT * 5;
const MENU_PADDING_Y = 8;

export const ClientSelector = (): ReactNode => {
  const [showMenu, setShowMenu] = useNavTopMenu(NavMenuKey.CLIENT_SELECTOR);
  const { hoverHandlers, consumeHoverOpen } = useNavHoverOpen(showMenu, () => setShowMenu(true));
  const allUserCompanies = useAppSelector(UserRedux.companiesSelector);
  const { cid, color, name } = useCompanyInfo();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUserCompanies = useFilteredList(allUserCompanies, searchQuery, {
    keys: ['company']
  });

  const icon = cid ? (
    <ClientLogo
      cid={cid}
      background={Background.DARK}
      unloader={<Avatar name={cid} color={color} className='logo' />}
    />
  ) : (
    <MdLanguage />
  );
  const labelCopy = cid ? 'Switch client' : 'Pick a client';

  return (
    <>
      <IconButton
        {...hoverHandlers}
        data-testid='client-selector-button'
        className={cn('client-selector', { active: showMenu })}
        appearance={Appearance.EMPTY}
        background={Background.DARK}
        onClick={() => {
          hoverHandlers.onClick();
          if (consumeHoverOpen() && showMenu) return;
          setShowMenu(!showMenu);
        }}
        aria-label={labelCopy}
        tooltip={name || cid ? { text: name || cid, placement: Placement.BOTTOM } : undefined}
        ref={buttonRef}
      >
        {icon}
      </IconButton>
      <Overlay
        target={buttonRef}
        show={showMenu}
        placement={Placement.RIGHT_START}
        popperConfig={NAV_POPOVER_CONFIG}
      >
        {props => (
          <Popover
            id='client-selector-popover'
            className='nav-popover client-selector-popover'
            {...props}
          >
            {allUserCompanies.length > 10 && (
              <Popover.Header>
                <SearchField
                  size={Size.LG}
                  id='client-selector-search'
                  onChange={e => {
                    setSearchQuery(e.currentTarget.value);
                  }}
                  value={searchQuery}
                  background={Background.DARK}
                  placeholder='Search my clients'
                  autoFocus
                />
              </Popover.Header>
            )}
            <Popover.Body>
              <ClientSelectorMenu userCompanies={filteredUserCompanies} />
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </>
  );
};

interface ClientSelectorMenuProps {
  userCompanies: readonly UserRedux.UserCompany[];
}

const ClientSelectorMenu = ({ userCompanies }: ClientSelectorMenuProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const listHeight = Math.min(userCompanies.length * ITEM_HEIGHT, MAX_LIST_HEIGHT);

  const [virtual, , setTop] = useVirtual({
    overscan: OVERSCAN,
    columnCount: 1,
    columnWidth: 1,
    width: 1,
    rowCount: userCompanies.length,
    rowHeight: ITEM_HEIGHT,
    height: listHeight
  });

  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setTop(0);
  }, [userCompanies, setTop]);

  if (userCompanies.length === 0) {
    return <EmptySearch />;
  }

  return (
    <div
      ref={scrollRef}
      className='nav-group-menu virtualized-menu'
      // border-box, so the height must include the padding
      style={{ height: listHeight + MENU_PADDING_Y * 2, paddingBlock: MENU_PADDING_Y }}
      onScroll={e => setTop(e.currentTarget.scrollTop)}
    >
      <div
        style={{ height: userCompanies.length * ITEM_HEIGHT }}
        className='virtualized-menu-inner'
      >
        {userCompanies
          .slice(virtual.rows.visible.min, virtual.rows.visible.max + 1)
          .map(({ company: userCompany }, offset) => {
            const index = virtual.rows.visible.min + offset;
            return (
              <div
                key={userCompany}
                className='virtualized-menu-item'
                style={{
                  top: virtual.rows.positions[index],
                  height: ITEM_HEIGHT
                }}
              >
                <ClientSelectorMenuItem userCompany={userCompany} />
              </div>
            );
          })}
      </div>
    </div>
  );
};

interface ClientSelectorMenuItemProps {
  userCompany: string;
}

const ClientSelectorMenuItem = ({ userCompany }: ClientSelectorMenuItemProps) => {
  const [, setShowMenu] = useNavTopMenu(NavMenuKey.CLIENT_SELECTOR);
  const { queuedNavigation, setQueuedNavigation } = useNavBarState();
  const closeNav = useCloseNav();
  const navigate = useNavigate();
  const location = useLocation();
  const { cid } = useCompanyInfo();
  const enableClientActivityFeed = Boolean(useExperimentFlag('enableActivityFeed'));

  const selectClient = () => {
    try {
      // Replace current client
      if (cid) {
        navigate(location.pathname.replace(cid, userCompany), { replace: true });
        return;
      }

      // Trigger queued navigation to client-page
      if (queuedNavigation) {
        navigate(`/${userCompany}/${queuedNavigation.path}`);
        setQueuedNavigation(undefined);
        closeNav();
        return;
      }

      if (enableClientActivityFeed) {
        navigate(`/${userCompany}/activity-feed`);
        return;
      }

      // Nav to root for new client; index route redirects to the default page
      navigate(`/${userCompany}`);
    } finally {
      setShowMenu(false);
    }
  };

  return (
    <div className='nav-menu-item-container'>
      <Button
        size={Size.LG}
        appearance={Appearance.EMPTY}
        background={Background.DARK}
        leadingElement={
          <ClientLogo
            cid={userCompany}
            background={Background.DARK}
            unloader={
              <IconButton
                background={Background.DARK}
                onClick={() => {
                  navigate(`/image-uploader?client=${userCompany}`);
                  closeNav();
                }}
              >
                <MdOutlineAddPhotoAlternate />
              </IconButton>
            }
          />
        }
        leadingElementIsInteractive
        className={cn('nav-menu-item')}
        onClick={selectClient}
      >
        <span className='nav-menu-label'>{userCompany}</span>
      </Button>
    </div>
  );
};
